import { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const HeroCanvas = lazy(() => import("@/components/HeroCanvas"));

const VERTICALS = [
  ["I", "Mobility Access & Transition"],
  ["II", "Ecological Infrastructure"],
  ["III", "Human Capital & Transition"],
  ["IV", "Policy & Sovereign Collaboration"],
  ["V", "Cultural & Civilizational Continuity"],
];

const ERA_OLD = [
  "Oil & fossil energy",
  "Centralized production",
  "Industrial speed",
  "Financial acceleration",
  "Extractive growth",
];
const ERA_NEW = [
  "Climate resilience",
  "Decentralized energy",
  "Ecological recovery",
  "Sovereign sustainability",
  "Social continuity",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative w-full overflow-hidden"
        style={{ minHeight: "100vh", paddingTop: 168 }}
      >
        {/* 3D canvas constrained to right portion on desktop, faint background on mobile */}
        <div
          className="absolute inset-y-0 right-0 hidden md:block"
          style={{ width: "60%", pointerEvents: "none" }}
        >
          <Suspense fallback={<div style={{ background: "var(--pearl)" }} />}>
            <HeroCanvas />
          </Suspense>
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(247,247,247,1) 0%, rgba(247,247,247,0.6) 22%, rgba(247,247,247,0) 55%)",
            }}
          />
        </div>
        <div
          className="absolute inset-0 md:hidden"
          style={{ pointerEvents: "none", opacity: 0.35 }}
        >
          <Suspense fallback={<div style={{ background: "var(--pearl)" }} />}>
            <HeroCanvas />
          </Suspense>
        </div>

        <div
          className="container-wide relative z-10 flex flex-col justify-end"
          style={{ minHeight: "calc(100vh - 168px)", paddingBottom: "6rem" }}
        >
          <div className="max-w-3xl">
            <Reveal>
              <p className="label-mono mb-6">
                — Folks Foundation
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1
                className="font-display"
                style={{
                  fontSize: "clamp(2.25rem, 6.4vw, 6rem)",
                  lineHeight: 0.94,
                  letterSpacing: "-0.045em",
                  color: "var(--charcoal)",
                }}
              >
                Where industrial
                <br />
                transformation meets{" "}
                <span style={{ color: "var(--mulberry)" }}>
                  ecological salvation.
                </span>
              </h1>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
                <p className="md:col-span-7 font-body text-xl md:text-2xl leading-snug max-w-xl">
                  A long-duration regenerative institution aligning capital,
                  infrastructure, technology, ecology, and human progress into
                  one civilizational framework.
                </p>
                <div className="md:col-span-5 flex flex-wrap gap-4">
                  <Link
                    to="/vision"
                    data-testid="hero-cta-vision"
                    className="btn-mulberry"
                  >
                    Read the thesis <ArrowUpRight size={14} />
                  </Link>
                  <Link
                    to="/about"
                    data-testid="hero-cta-about"
                    className="btn-ghost-dark"
                  >
                    The institution
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section
        data-testid="marquee-section"
        style={{
          background: "var(--charcoal)",
          color: "var(--pearl)",
          overflow: "hidden",
          borderTop: "1px solid var(--soft-border-inv)",
          borderBottom: "1px solid var(--soft-border-inv)",
        }}
      >
        <div
          className="flex marquee-track whitespace-nowrap py-3 font-display"
          style={{
            fontSize: "clamp(0.875rem, 1.6vw, 1.25rem)",
            letterSpacing: "0.04em",
            fontWeight: 500,
            width: "max-content",
          }}
        >
          {Array.from({ length: 2 }).map((_, idx) => (
            <div key={idx} className="flex items-center gap-12 pr-12">
              <span>RESTORE</span>
              <span style={{ color: "var(--mulberry-bright)" }}>·</span>
              <span>STABILIZE</span>
              <span style={{ color: "var(--mulberry-bright)" }}>·</span>
              <span>DECENTRALIZE</span>
              <span style={{ color: "var(--mulberry-bright)" }}>·</span>
              <span>COMPOUND</span>
              <span style={{ color: "var(--mulberry-bright)" }}>·</span>
              <span>REGENERATE</span>
              <span style={{ color: "var(--mulberry-bright)" }}>·</span>
            </div>
          ))}
        </div>
      </section>

      {/* ONE BELIEF */}
      <section
        data-testid="one-belief"
        className="container-wide"
        style={{ paddingTop: "10rem", paddingBottom: "10rem" }}
      >
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <p className="label-mono">— One Belief / 01</p>
          </div>
          <div className="md:col-span-9">
            <Reveal>
              <p
                className="font-body italic"
                style={{
                  fontSize: "clamp(1.75rem, 3.6vw, 3.25rem)",
                  lineHeight: 1.15,
                  color: "var(--charcoal)",
                }}
              >
                “The future cannot survive on economic growth alone. It must
                compound{" "}
                <span style={{ color: "var(--mulberry)" }}>
                  ecological and human value.
                </span>
                ”
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="label-mono mt-8">
                — The industrial age optimized for extraction. The next era will
                be defined by regeneration.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ERA SHIFT — Editorial prose, no grid / no table */}
      <section
        data-testid="era-shift"
        style={{ position: "relative", overflow: "hidden" }}
      >
        {/* Old era — dark prose paragraph */}
        <div
          style={{
            background: "var(--charcoal)",
            color: "var(--pearl)",
            padding: "8rem 0",
          }}
        >
          <div className="container-wide">
            <Reveal>
              <p className="label-mono-light mb-10">
                — 20th Century / Optimized for
              </p>
              <p
                className="font-display"
                style={{
                  fontSize: "clamp(1.75rem, 4.5vw, 4rem)",
                  letterSpacing: "-0.035em",
                  lineHeight: 1.05,
                  color: "rgba(247,247,247,0.55)",
                }}
              >
                <span>The industrial age was built on </span>
                {ERA_OLD.map((item, i) => (
                  <span key={i}>
                    <span
                      style={{
                        textDecoration: "line-through",
                        textDecorationColor: "rgba(247,247,247,0.3)",
                        textDecorationThickness: "2px",
                        color: "rgba(247,247,247,0.7)",
                      }}
                    >
                      {item.toLowerCase()}
                    </span>
                    {i < ERA_OLD.length - 2
                      ? ", "
                      : i === ERA_OLD.length - 2
                        ? ", and "
                        : "."}
                  </span>
                ))}
              </p>
            </Reveal>
          </div>
        </div>

        {/* New era — light prose paragraph */}
        <div
          style={{
            background: "var(--pearl-2)",
            color: "var(--charcoal)",
            padding: "8rem 0",
          }}
        >
          <div className="container-wide">
            <Reveal delay={0.1}>
              <p
                className="label-mono mb-10"
                style={{ color: "var(--mulberry)" }}
              >
                — 21st Century / Will Optimize for
              </p>
              <p
                className="font-display"
                style={{
                  fontSize: "clamp(1.75rem, 4.5vw, 4rem)",
                  letterSpacing: "-0.035em",
                  lineHeight: 1.05,
                  color: "var(--charcoal)",
                }}
              >
                <span>The next era will be defined by </span>
                {ERA_NEW.map((item, i) => (
                  <span key={i}>
                    <span
                      className="font-bold"
                      style={{ color: "var(--mulberry)" }}
                    >
                      {item.toLowerCase()}
                    </span>
                    {i < ERA_NEW.length - 2
                      ? ", "
                      : i === ERA_NEW.length - 2
                        ? ", and "
                        : "."}
                  </span>
                ))}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="label-mono mt-12 max-w-2xl opacity-70">
                — Five reversals. One operating system for a regenerative
                century.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VERTICALS PREVIEW */}
      <section
        data-testid="verticals-preview"
        className="container-wide"
        style={{ paddingTop: "10rem", paddingBottom: "10rem" }}
      >
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-4">
            <p className="label-mono mb-4">— Five Regenerative Verticals</p>
            <h2
              className="font-display font-bold"
              style={{
                fontSize: "clamp(2rem, 4vw, 3.25rem)",
                letterSpacing: "-0.035em",
                lineHeight: 0.95,
              }}
            >
              Five systems
              <br />
              the next civilization
              <br />
              <span style={{ color: "var(--mulberry)" }}>depends on.</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 self-end">
            <p className="font-body text-xl leading-relaxed max-w-xl">
              Mobility, ecology, human capital, policy and culture — each a
              distinct lever, together a coherent operating system for a
              regenerative century.
            </p>
            <Link
              to="/vision"
              data-testid="verticals-cta"
              className="btn-ghost-dark mt-8"
            >
              Explore the vision <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        <div className="divider-line">
          {VERTICALS.map(([num, label]) => (
            <Reveal key={num}>
              <Link
                to="/vision"
                data-testid={`vertical-row-${num}`}
                className="group block border-b border-[color:var(--soft-border)] py-8 md:py-10"
              >
                <div className="grid grid-cols-12 items-baseline gap-6">
                  <span className="pillar-num col-span-2 md:col-span-1">
                    {num}
                  </span>
                  <h3
                    className="col-span-9 md:col-span-9 font-display font-bold"
                    style={{
                      fontSize: "clamp(1.5rem, 3.2vw, 2.75rem)",
                      letterSpacing: "-0.03em",
                      lineHeight: 1,
                      transition: "color 0.5s cubic-bezier(0.16,1,0.3,1)",
                    }}
                  >
                    <span className="group-hover:text-[color:var(--mulberry)]">
                      {label}
                    </span>
                  </h3>
                  <span
                    className="col-span-1 md:col-span-2 justify-self-end"
                    style={{ transition: "transform 0.5s" }}
                  >
                    <ArrowUpRight
                      size={20}
                      className="group-hover:translate-x-1 group-hover:-translate-y-1"
                      style={{ color: "var(--mulberry)" }}
                    />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLOSING */}
      <section
        data-testid="home-closing"
        style={{
          background: "var(--charcoal)",
          color: "var(--pearl)",
          padding: "8rem 0",
          position: "relative",
        }}
      >
        <div className="container-wide">
          <Reveal>
            <p className="label-mono-light">— The civilizational shift</p>
            <h2
              className="font-display font-bold mt-6"
              style={{
                fontSize: "clamp(2.25rem, 6vw, 5rem)",
                letterSpacing: "-0.04em",
                lineHeight: 0.95,
              }}
            >
              Most institutions are still built
              <br />
              for the old world.{" "}
              <span style={{ color: "var(--mulberry-bright)" }}>
                Folks Foundation is being built for the next one.
              </span>
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/future" data-testid="closing-cta-future" className="btn-ghost-light">
                The long-duration bet <ArrowUpRight size={14} />
              </Link>
              <Link to="/contact" data-testid="closing-cta-contact" className="btn-ghost-light">
                Engage the foundation
              </Link>
            </div>
          </Reveal>

          {/* Part of Folks Corporation — home page bottom right */}
          <div className="mt-20 md:mt-24 flex justify-end">
            <a
              href="https://www.folkscorporation.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-folks-corp"
              className="flex items-center gap-4 group"
            >
              <span className="label-mono-light leading-tight text-right">
                Part of
                <br />
                <span
                  className="link-underline"
                  style={{ color: "var(--mulberry-bright)" }}
                >
                  Folks Corporation
                </span>
              </span>
              <img
                src="/folks_corporation.png"
                alt="Folks Corporation"
                style={{
                  height: 64,
                  width: "auto",
                  objectFit: "contain",
                  opacity: 0.9,
                  transition: "opacity 0.4s cubic-bezier(0.16,1,0.3,1)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.9")}
              />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
